import React, { useState, useEffect, useRef } from 'react';
import { Package, Plus, Edit2, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, Star, List, Search } from 'lucide-react';
import toast from 'react-hot-toast';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [productForm, setProductForm] = useState({
    name: '',
    category: '',
    status: 'undetected',
    max_discount: '',
    featured: 0,
    description_data: {
      requirements: '',
      information: '',
      aimbot: '',
      visuals: '',
      world: '',
      configs: '',
      misc: ''
    }
  });
  const [variantForm, setVariantForm] = useState({
    name: '',
    price: '',
    backend_cost: '',
    api_endpoint: '',
    api_method: 'POST',
    parameters: '{}',
    stock_type: 'api',
    api_link_prefix: ''
  });
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockVariant, setStockVariant] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [newKeys, setNewKeys] = useState('');
  const [scrollPosition, setScrollPosition] = useState(0);
  const [stockCounts, setStockCounts] = useState({});
  const [showAllProductsModal, setShowAllProductsModal] = useState(false);
  const [viewAllFilters, setViewAllFilters] = useState({
    selectedProducts: [],
    searchQuery: '',
    stockComparison: 'any',
    stockValue: '',
    showOnlyMatchingVariants: false
  });
  const [productFilterDropdownOpen, setProductFilterDropdownOpen] = useState(false);
  const [stockFilterDropdownOpen, setStockFilterDropdownOpen] = useState(false);
  const productFilterDropdownRef = useRef(null);
  const stockFilterDropdownRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productFilterDropdownRef.current && !productFilterDropdownRef.current.contains(event.target)) {
        setProductFilterDropdownOpen(false);
      }
      if (stockFilterDropdownRef.current && !stockFilterDropdownRef.current.contains(event.target)) {
        setStockFilterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async (preserveScroll = false) => {
    try {
      if (preserveScroll) {
        setScrollPosition(window.scrollY);
      }
      setLoading(true);
      const token = sessionStorage.getItem('token');
      const response = await fetch('/api/admin/products', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setProducts(data.data.products);
        
        // Fetch stock counts for manual variants in one batch request
        const token = sessionStorage.getItem('token');
        const manualVariantIds = [];
        
        // Collect all manual variant IDs
        for (const product of data.data.products) {
          for (const variant of product.variants || []) {
            if (variant.stock_type === 'manual') {
              manualVariantIds.push(variant.id);
            }
          }
        }
        
        // Fetch all stock counts in one request
        if (manualVariantIds.length > 0) {
          try {
            const stockResponse = await fetch('/api/admin/stock/variants/batch-counts', {
              method: 'POST',
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ variantIds: manualVariantIds })
            });
            const stockData = await stockResponse.json();
            if (stockData.success) {
              setStockCounts(stockData.data);
            }
          } catch (err) {
            console.error('Error fetching stock counts:', err);
          }
        }
        
        if (preserveScroll) {
          setTimeout(() => {
            window.scrollTo(0, scrollPosition);
          }, 0);
        }
      } else {
        toast.error('Failed to load products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Error loading products');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = () => {
    setSelectedProduct(null);
    setProductForm({
      name: '',
      category: '',
      status: 'undetected',
      max_discount: '',
      featured: 0
    });
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setProductForm({
      name: product.name,
      category: product.category || '',
      status: product.status,
      max_discount: product.max_discount || '',
      featured: product.featured || 0
    });
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.name) {
      toast.error('Product name is required');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const url = selectedProduct 
        ? `/api/admin/products/${selectedProduct.id}`
        : '/api/admin/products';
      const method = selectedProduct ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...productForm,
          max_discount: productForm.max_discount ? parseFloat(productForm.max_discount) : null
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(selectedProduct ? 'Product updated' : 'Product created');
        setShowProductModal(false);
        fetchProducts();
      } else {
        toast.error(data.message || 'Failed to save product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Error saving product');
    }
  };

  const handleSaveDescription = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/products/${selectedProduct.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: productForm.name,
          category: productForm.category,
          status: productForm.status,
          max_discount: productForm.max_discount ? parseFloat(productForm.max_discount) : null,
          featured: productForm.featured,
          description_data: productForm.description_data
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Product description updated');
        setShowDescriptionModal(false);
        fetchProducts();
      } else {
        toast.error(data.message || 'Failed to update description');
      }
    } catch (error) {
      console.error('Error saving description:', error);
      toast.error('Error saving description');
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete "${productName}"? This will also delete all variants.`)) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Product deleted');
        fetchProducts();
      } else {
        toast.error(data.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Error deleting product');
    }
  };

  const handleCreateVariant = (product) => {
    setSelectedProduct(product);
    setSelectedVariant(null);
    setVariantForm({
      name: '',
      description: '',
      price: '',
      backend_cost: '',
      api_endpoint: '',
      api_method: 'POST',
      parameters: '{}',
      stock_type: 'api'
    });
    setShowVariantModal(true);
  };

  const handleEditVariant = (product, variant) => {
    setSelectedProduct(product);
    setSelectedVariant(variant);
    setVariantForm({
      name: variant.name,
      price: variant.price,
      backend_cost: variant.backend_cost || '',
      api_endpoint: variant.api_endpoint || '',
      api_method: variant.api_method || 'POST',
      parameters: typeof variant.parameters === 'object' ? JSON.stringify(variant.parameters, null, 2) : variant.parameters,
      stock_type: variant.stock_type || 'api',
      api_link_prefix: variant.api_link_prefix || ''
    });
    setShowVariantModal(true);
  };

  const handleSaveVariant = async () => {
    if (!variantForm.name || !variantForm.price) {
      toast.error('Variant name and price are required');
      return;
    }

    if (variantForm.stock_type === 'api' && !variantForm.api_endpoint) {
      toast.error('API endpoint is required for API stock type');
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const url = selectedVariant
        ? `/api/admin/products/${selectedProduct.id}/variants/${selectedVariant.id}`
        : `/api/admin/products/${selectedProduct.id}/variants`;
      const method = selectedVariant ? 'PATCH' : 'POST';

      let parameters = variantForm.parameters;
      try {
        parameters = JSON.parse(variantForm.parameters);
      } catch (e) {
        toast.error('Invalid JSON in parameters field');
        return;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...variantForm,
          price: parseFloat(variantForm.price),
          backend_cost: variantForm.backend_cost ? parseFloat(variantForm.backend_cost) : 0,
          parameters
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(selectedVariant ? 'Variant updated' : 'Variant created');
        setShowVariantModal(false);
        fetchProducts(true);
      } else {
        toast.error(data.message || 'Failed to save variant');
      }
    } catch (error) {
      console.error('Error saving variant:', error);
      toast.error('Error saving variant');
    }
  };

  const handleDeleteVariant = async (productId, variantId, variantName) => {
    if (!window.confirm(`Are you sure you want to delete variant "${variantName}"?`)) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/products/${productId}/variants/${variantId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Variant deleted');
        fetchProducts(true);
      } else {
        toast.error(data.message || 'Failed to delete variant');
      }
    } catch (error) {
      console.error('Error deleting variant:', error);
      toast.error('Error deleting variant');
    }
  };

  const handleMoveVariant = async (productId, variantId, direction) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/products/${productId}/variants/${variantId}/order`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ direction })
      });

      const data = await response.json();
      if (data.success) {
        fetchProducts(true);
      } else {
        toast.error(data.message || 'Failed to reorder variant');
      }
    } catch (error) {
      console.error('Error reordering variant:', error);
      toast.error('Error reordering variant');
    }
  };

  const handleMoveProduct = async (productId, direction) => {
    try {
      const scrollPosition = window.scrollY;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/products/${productId}/order`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ direction })
      });

      const data = await response.json();
      if (data.success) {
        await fetchProducts();
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPosition);
        });
      } else {
        toast.error(data.message || 'Failed to reorder product');
      }
    } catch (error) {
      console.error('Error reordering product:', error);
      toast.error('Error reordering product');
    }
  };

  const toggleProductExpand = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handleManageStock = async (variant) => {
    setStockVariant(variant);
    setShowStockModal(true);
    
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/stock/variants/${variant.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setStockData(data.data);
      } else {
        toast.error('Failed to load stock data');
      }
    } catch (error) {
      console.error('Error fetching stock:', error);
      toast.error('Error loading stock data');
    }
  };

  const handleAddKeys = async () => {
    if (!newKeys.trim()) {
      toast.error('Please enter at least one key');
      return;
    }

    const keysArray = newKeys.split('\n').map(k => k.trim()).filter(k => k);
    
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/stock/variants/${stockVariant.id}/keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ keys: keysArray })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Added ${keysArray.length} key(s)`);
        setNewKeys('');
        handleManageStock(stockVariant);
      } else {
        toast.error(data.message || 'Failed to add keys');
      }
    } catch (error) {
      console.error('Error adding keys:', error);
      toast.error('Error adding keys');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this key?')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/stock/keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Key deleted');
        handleManageStock(stockVariant);
      } else {
        toast.error(data.message || 'Failed to delete key');
      }
    } catch (error) {
      console.error('Error deleting key:', error);
      toast.error('Error deleting key');
    }
  };

  const handleDeleteAllUsedKeys = async () => {
    if (!stockData || !stockData.keys) return;
    
    const usedKeys = stockData.keys.filter(k => k.status === 'used');
    if (usedKeys.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete all ${usedKeys.length} used keys?`)) {
      return;
    }

    const token = sessionStorage.getItem('token');
    let deleted = 0;
    let failed = 0;
    
    for (const key of usedKeys) {
      try {
        const response = await fetch(`/api/admin/stock/keys/${key.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success) {
          deleted++;
        } else {
          failed++;
        }
      } catch (error) {
        console.error('Error deleting key:', key.id, error);
        failed++;
      }
    }
    
    if (deleted > 0) {
      toast.success(`Deleted ${deleted} used keys${failed > 0 ? ` (${failed} failed)` : ''}`);
    } else {
      toast.error('Failed to delete used keys');
    }
    handleManageStock(stockVariant);
  };

  const toggleFeatured = async (productId, currentFeatured) => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ featured: currentFeatured ? 0 : 1 })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(currentFeatured ? 'Removed from featured' : 'Added to featured');
        fetchProducts(true);
      } else {
        toast.error(data.message || 'Failed to update featured status');
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast.error('Error updating featured status');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      undetected: { bg: '#22c55e', text: 'Undetected' },
      detected: { bg: '#ef4444', text: 'Detected' },
      updating: { bg: '#60a5fa', text: 'Updating' },
      testing: { bg: '#60a5fa', text: 'Testing' },
      use_at_own_risk: { bg: '#eab308', text: 'Use At Own Risk' }
    };

    const style = styles[status] || styles.undetected;

    return (
      <span style={{
        backgroundColor: style.bg,
        color: '#ffffff',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500'
      }}>
        {style.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', color: '#ffffff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Package className="h-6 w-6" />
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Product Management</h2>
        </div>
        <p style={{ color: '#9ca3af', marginTop: '16px' }}>Loading products...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Package className="h-6 w-6" style={{ color: '#3b198e' }} />
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0, color: '#ffffff' }}>
              Product Management
            </h2>
          </div>
          <p style={{ color: '#9ca3af', margin: 0 }}>
            Manage products, variants, pricing, and discounts
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowAllProductsModal(true)}
            style={{
              backgroundColor: '#4a4a4a',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a5a5a'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4a4a4a'}
          >
            <List className="h-4 w-4" />
            View All
          </button>
          <button
            onClick={handleCreateProduct}
            style={{
              backgroundColor: '#3b198e',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d1270'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b198e'}
          >
            <Plus className="h-4 w-4" />
            New Product
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a', borderRadius: '8px', padding: '16px' }}>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 8px 0' }}>Total Products</p>
          <p style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>{products.length}</p>
        </div>
        <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a', borderRadius: '8px', padding: '16px' }}>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 8px 0' }}>Undetected Products</p>
          <p style={{ color: '#22c55e', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
            {products.filter(p => p.status === 'undetected').length}
          </p>
        </div>
        <div style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a', borderRadius: '8px', padding: '16px' }}>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 8px 0' }}>Total Variants</p>
          <p style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
            {products.reduce((sum, p) => sum + (p.variants?.length || 0), 0)}
          </p>
        </div>
      </div>

      {/* Products List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {products.map((product) => (
          <div key={product.id} style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a', borderRadius: '8px', overflow: 'hidden' }}>
            {/* Product Header */}
            <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: expandedProducts[product.id] ? '1px solid #4a4a4a' : 'none' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h3 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', margin: 0 }}>{product.name}</h3>
                  {getStatusBadge(product.status)}
                  {product.max_discount && (
                    <span style={{ backgroundColor: '#7c3aed', color: '#ffffff', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>
                      Max Discount: {product.max_discount}%
                    </span>
                  )}
                </div>
                <p style={{ color: '#9ca3af', fontSize: '14px', margin: '0 0 8px 0' }}>{product.description || 'No description'}</p>
                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#9ca3af' }}>
                  <span>Category: {product.category || 'N/A'}</span>
                  <span>Variants: {product.variants?.length || 0}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => toggleFeatured(product.id, product.featured)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title={product.featured === 1 ? "Remove from featured" : "Add to featured"}
                >
                  <Star 
                    className="h-5 w-5" 
                    style={{
                      color: product.featured === 1 ? '#fbbf24' : '#9ca3af',
                      fill: product.featured === 1 ? '#fbbf24' : 'none',
                      strokeWidth: 2
                    }}
                  />
                </button>
                <button
                  onClick={() => handleMoveProduct(product.id, 'up')}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Move up"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleMoveProduct(product.id, 'down')}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#9ca3af',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Move down"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setSelectedProduct(product);
                    const descData = product.description_data 
                      ? (typeof product.description_data === 'string' 
                          ? JSON.parse(product.description_data) 
                          : product.description_data)
                      : {
                          requirements: '',
                          information: '',
                          aimbot: '',
                          visuals: '',
                          world: '',
                          configs: '',
                          misc: ''
                        };
                    setProductForm({
                      name: product.name,
                      category: product.category,
                      status: product.status,
                      max_discount: product.max_discount || '',
                      featured: product.featured || 0,
                      description_data: descData
                    });
                    setShowDescriptionModal(true);
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #4a4a4a',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Edit2 className="h-4 w-4" />
                  Edit Description
                </button>
                <button
                  onClick={() => toggleProductExpand(product.id)}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #4a4a4a',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {expandedProducts[product.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  {expandedProducts[product.id] ? 'Hide' : 'Show'} Variants
                </button>
                <button
                  onClick={() => handleEditProduct(product)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#3b198e',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Edit product"
                >
                  <Edit2 className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleDeleteProduct(product.id, product.name)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Delete product"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Variants Section */}
            {expandedProducts[product.id] && (
              <div style={{ padding: '16px', backgroundColor: '#0a0a0a' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', margin: 0 }}>Variants</h4>
                  <button
                    onClick={() => handleCreateVariant(product)}
                    style={{
                      backgroundColor: '#3b198e',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    <Plus className="h-3 w-3" />
                    Add Variant
                  </button>
                </div>

                {product.variants && product.variants.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {product.variants.map((variant) => (
                      <div key={variant.id} style={{ backgroundColor: '#1a1a1a', border: '1px solid #4a4a4a', borderRadius: '6px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <span style={{ color: '#ffffff', fontWeight: '500' }}>{variant.name}</span>
                            {getStatusBadge(variant.status)}
                            <span style={{ color: '#22c55e', fontWeight: '600' }}>${parseFloat(variant.price).toFixed(2)}</span>
                          </div>
                          <p style={{ color: '#9ca3af', fontSize: '12px', margin: '0 0 4px 0' }}>{variant.description || 'No description'}</p>
                          <div style={{ fontSize: '11px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{variant.stock_type === 'manual' ? 'Manual Stock' : `API: ${variant.api_endpoint || 'Not set'}`}</span>
                            {variant.stock_type === 'manual' && (
                              <span style={{ 
                                backgroundColor: stockCounts[variant.id]?.available > 0 ? '#22c55e' : '#ef4444', 
                                color: '#ffffff', 
                                padding: '2px 6px', 
                                borderRadius: '4px', 
                                fontSize: '10px',
                                fontWeight: '600'
                              }}>
                                {stockCounts[variant.id] 
                                  ? `Stock: ${stockCounts[variant.id].available}`
                                  : 'Stock: Loading...'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {variant.stock_type === 'manual' && (
                            <button
                              onClick={() => handleManageStock(variant)}
                              style={{
                                backgroundColor: '#3b198e',
                                border: 'none',
                                color: '#ffffff',
                                cursor: 'pointer',
                                padding: '6px 10px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '500',
                                marginRight: '4px'
                              }}
                              title="Manage stock"
                            >
                              Manage Stock
                            </button>
                          )}
                          <button
                            onClick={() => handleMoveVariant(product.id, variant.id, 'up')}
                            style={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#9ca3af',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleMoveVariant(product.id, variant.id, 'down')}
                            style={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#9ca3af',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEditVariant(product, variant)}
                            style={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#3b198e',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Edit variant"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteVariant(product.id, variant.id, variant.name)}
                            style={{
                              backgroundColor: 'transparent',
                              border: 'none',
                              color: '#ef4444',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Delete variant"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', padding: '16px' }}>No variants yet</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: 'bold', marginBottom: '16px' }}>
              {selectedProduct ? 'Edit Product' : 'Create Product'}
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Product Name *
              </label>
              <input
                type="text"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Category
              </label>
              <input
                type="text"
                value={productForm.category}
                onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Max Discount (%) - Overrides user discount
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={productForm.max_discount}
                onChange={(e) => setProductForm({ ...productForm, max_discount: e.target.value })}
                placeholder="Leave empty for no limit"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Status
              </label>
              <select
                value={productForm.status}
                onChange={(e) => setProductForm({ ...productForm, status: e.target.value })}
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              >
                <option value="undetected">Undetected</option>
                <option value="detected">Detected</option>
                <option value="updating">Updating</option>
                <option value="testing">Testing</option>
                <option value="use_at_own_risk">Use At Own Risk</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSaveProduct}
                style={{
                  flex: 1,
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
              <button
                onClick={() => setShowProductModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: '#4a4a4a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Variant Modal */}
      {showVariantModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
              {selectedVariant ? 'Edit Variant' : 'Create Variant'}
            </h3>
            <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '16px' }}>
              Product: {selectedProduct?.name}
            </p>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Variant Name *
              </label>
              <input
                type="text"
                value={variantForm.name}
                onChange={(e) => setVariantForm({ ...variantForm, name: e.target.value })}
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>


            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Price ($) *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={variantForm.price}
                onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })}
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Backend Cost ($)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={variantForm.backend_cost}
                onChange={(e) => setVariantForm({ ...variantForm, backend_cost: e.target.value })}
                placeholder="Cost to fulfill this variant from your backend API"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
              <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '8px' }}>
                The cost YOU pay to fulfill this variant. Used to calculate net profit.
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Stock Type *
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setVariantForm({ ...variantForm, stock_type: 'api' })}
                  style={{
                    flex: 1,
                    backgroundColor: variantForm.stock_type === 'api' ? '#3b82f6' : '#0a0a0a',
                    color: '#ffffff',
                    border: `1px solid ${variantForm.stock_type === 'api' ? '#3b82f6' : '#4a4a4a'}`,
                    borderRadius: '6px',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  API Stock
                </button>
                <button
                  type="button"
                  onClick={() => setVariantForm({ ...variantForm, stock_type: 'manual' })}
                  style={{
                    flex: 1,
                    backgroundColor: variantForm.stock_type === 'manual' ? '#3b82f6' : '#0a0a0a',
                    color: '#ffffff',
                    border: `1px solid ${variantForm.stock_type === 'manual' ? '#3b82f6' : '#4a4a4a'}`,
                    borderRadius: '6px',
                    padding: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  Manual Stock
                </button>
              </div>
              <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '8px' }}>
                {variantForm.stock_type === 'api' 
                  ? 'Keys are automatically purchased from external API when users buy this variant'
                  : 'You manually add keys to stock and they are assigned when users purchase'}
              </p>
            </div>

            {variantForm.stock_type === 'api' && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                    API Endpoint *
                  </label>
              <input
                type="text"
                value={variantForm.api_endpoint}
                onChange={(e) => setVariantForm({ ...variantForm, api_endpoint: e.target.value })}
                placeholder="/api/endpoint"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                API Method
              </label>
              <select
                value={variantForm.api_method}
                onChange={(e) => setVariantForm({ ...variantForm, api_method: e.target.value })}
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none'
                }}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Parameters (JSON)
              </label>
              <textarea
                value={variantForm.parameters}
                onChange={(e) => setVariantForm({ ...variantForm, parameters: e.target.value })}
                rows={4}
                placeholder='{"key": "value"}'
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '12px',
                  outline: 'none',
                  fontFamily: 'monospace',
                  resize: 'vertical'
                }}
              />
            </div>
              </>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                API Link Prefix (Optional)
              </label>
              <input
                type="text"
                value={variantForm.api_link_prefix}
                onChange={(e) => setVariantForm({ ...variantForm, api_link_prefix: e.target.value })}
                placeholder="https://api.reign.reselling/api/v1/{product}/{variant}/{hash}"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '12px',
                  outline: 'none',
                  fontFamily: 'monospace'
                }}
              />
              <p style={{ color: '#6b7280', fontSize: '11px', marginTop: '6px' }}>
                Use placeholders: {'{product}'} {'{variant}'} {'{hash}'}. Leave empty for default format.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleSaveVariant}
                style={{
                  flex: 1,
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Save
              </button>
              <button
                onClick={() => setShowVariantModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: '#4a4a4a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stock Management Modal */}
      {showStockModal && stockVariant && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>
              Manage Stock - {stockVariant.name}
            </h3>
            <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '16px' }}>
              {stockData ? `${stockData.available} available / ${stockData.total} total keys` : 'Loading...'}
            </p>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Add Keys (one per line)
              </label>
              <textarea
                value={newKeys}
                onChange={(e) => setNewKeys(e.target.value)}
                rows={5}
                placeholder="KEY-1234-ABCD&#10;KEY-5678-EFGH&#10;KEY-9012-IJKL"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '12px',
                  outline: 'none',
                  fontFamily: 'monospace',
                  resize: 'vertical'
                }}
              />
              <button
                onClick={handleAddKeys}
                style={{
                  marginTop: '8px',
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Add Keys
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ color: '#ffffff', fontSize: '16px', fontWeight: '600', margin: 0 }}>
                  Stock Keys
                </h4>
                {stockData && stockData.keys && stockData.keys.filter(k => k.status === 'used').length > 0 && (
                  <button
                    onClick={handleDeleteAllUsedKeys}
                    style={{
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Delete All Used Keys
                  </button>
                )}
              </div>
              {stockData && stockData.keys && stockData.keys.length > 0 ? (
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {stockData.keys.map((key) => (
                    <div key={key.id} style={{
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '6px',
                      padding: '12px',
                      marginBottom: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#ffffff', fontFamily: 'monospace', fontSize: '12px', marginBottom: '4px' }}>
                          {key.key_value}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          Status: <span style={{ 
                            color: key.status === 'available' ? '#22c55e' : '#ef4444',
                            fontWeight: '500'
                          }}>
                            {key.status}
                          </span>
                          {key.assigned_at && ` • Assigned: ${new Date(key.assigned_at).toLocaleDateString()}`}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        style={{
                          backgroundColor: 'transparent',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          padding: '4px',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                  No keys in stock
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowStockModal(false);
                  setStockVariant(null);
                  setStockData(null);
                  setNewKeys('');
                }}
                style={{
                  flex: 1,
                  backgroundColor: '#4a4a4a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View All Products Modal */}
      {showAllProductsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '700px',
            height: '80vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '16px'
            }}>
              <div>
                <h3 style={{ color: '#ffffff', fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                  All Products & Variants
                </h3>
                <p style={{ color: '#9ca3af', fontSize: '14px', margin: '4px 0 0 0' }}>
                  {products.length} products • {products.reduce((sum, p) => sum + (p.variants?.length || 0), 0)} variants
                </p>
              </div>
              <button
                onClick={() => setShowAllProductsModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            {/* Filter Controls */}
            <div style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #4a4a4a',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '16px'
            }}>
              {/* Row 1: Product Multi-Select and Search */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                {/* Multi-Select Product Dropdown */}
                <div style={{ width: '50%', position: 'relative' }} ref={productFilterDropdownRef}>
                  <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Filter by Products
                  </label>
                  <div
                    onClick={() => setProductFilterDropdownOpen(!productFilterDropdownOpen)}
                    style={{
                      width: '100%',
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      padding: '0.5rem 2.5rem 0.5rem 1rem',
                      height: '80px',
                      cursor: 'pointer',
                      position: 'relative',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '4px',
                      alignItems: 'flex-start',
                      alignContent: 'flex-start',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      transition: 'border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
                  >
                    {viewAllFilters.selectedProducts.length === 0 ? (
                      <span style={{ color: '#9ca3af', fontSize: '14px' }}>All Products</span>
                    ) : (
                      viewAllFilters.selectedProducts.map(productId => {
                        const foundProduct = products.find(p => p.id === productId);
                        const displayName = foundProduct ? foundProduct.name : `ID:${productId}`;
                        return (
                          <span key={productId} style={{
                            backgroundColor: '#3b198e',
                            color: '#ffffff',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0
                          }}>
                            <span style={{ color: '#ffffff' }}>{displayName}</span>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewAllFilters(prev => ({
                                  ...prev,
                                  selectedProducts: prev.selectedProducts.filter(id => id !== productId)
                                }));
                              }}
                              style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', lineHeight: 1, color: '#ffffff' }}
                            >×</span>
                          </span>
                        );
                      })
                    )}
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#ffffff" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: productFilterDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                        width: '1.25rem',
                        height: '1.25rem',
                        transition: 'transform 0.2s ease',
                        pointerEvents: 'none'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  
                  {productFilterDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000
                    }}>
                      {products.map(product => (
                        <div
                          key={product.id}
                          onClick={() => {
                            setViewAllFilters(prev => ({
                              ...prev,
                              selectedProducts: prev.selectedProducts.includes(product.id)
                                ? prev.selectedProducts.filter(id => id !== product.id)
                                : [...prev.selectedProducts, product.id]
                            }));
                          }}
                          style={{
                            padding: '0.75rem 1rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            color: '#ffffff',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div style={{
                            width: '18px',
                            height: '18px',
                            border: '2px solid #4a4a4a',
                            borderRadius: '4px',
                            backgroundColor: viewAllFilters.selectedProducts.includes(product.id) ? '#3b198e' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {viewAllFilters.selectedProducts.includes(product.id) && (
                              <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                            )}
                          </div>
                          <span style={{ fontSize: '14px' }}>{product.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Search Bar */}
                <div style={{ width: '50%' }}>
                  <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Search Products
                  </label>
                  <div style={{ position: 'relative', height: '80px' }}>
                    <Search style={{ 
                      position: 'absolute', 
                      left: '12px', 
                      top: '12px', 
                      width: '18px', 
                      height: '18px', 
                      color: '#9ca3af',
                      zIndex: 1
                    }} />
                    {!viewAllFilters.searchQuery && (
                      <span style={{
                        position: 'absolute',
                        left: '2.5rem',
                        top: '10px',
                        color: '#9ca3af',
                        fontSize: '14px',
                        pointerEvents: 'none',
                        zIndex: 1
                      }}>Search</span>
                    )}
                    <textarea
                      value={viewAllFilters.searchQuery}
                      onChange={(e) => setViewAllFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                      style={{
                        width: '100%',
                        height: '80px',
                        backgroundColor: '#0a0a0a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '8px 1rem 8px 2.5rem',
                        color: '#ffffff',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'box-shadow 0.2s ease',
                        resize: 'none',
                        lineHeight: '1.4',
                        fontFamily: 'inherit'
                      }}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #3b198e'}
                      onBlur={(e) => e.target.style.boxShadow = ''}
                    />
                  </div>
                </div>
              </div>

              {/* Row 2: Stock Filter */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                <div style={{ width: '180px', position: 'relative' }} ref={stockFilterDropdownRef}>
                  <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '6px' }}>
                    Stock Filter
                  </label>
                  <div
                    onClick={() => setStockFilterDropdownOpen(!stockFilterDropdownOpen)}
                    style={{
                      width: '100%',
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      padding: '0.5rem 2.5rem 0.5rem 1rem',
                      height: '38px',
                      cursor: 'pointer',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
                  >
                    <span style={{ color: '#ffffff', fontSize: '14px' }}>
                      {{
                        'any': 'All Stock',
                        'greater': 'Greater Than',
                        'less': 'Less Than',
                        'equal': 'Equal To',
                        'greaterEqual': 'Equal/Greater Than',
                        'lessEqual': 'Equal/Less Than'
                      }[viewAllFilters.stockComparison] || 'All Stock'}
                    </span>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#ffffff" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: stockFilterDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                        width: '1.25rem',
                        height: '1.25rem',
                        transition: 'transform 0.2s ease',
                        pointerEvents: 'none'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  
                  {stockFilterDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000
                    }}>
                      {[
                        { value: 'any', label: 'All Stock' },
                        { value: 'greater', label: 'Greater Than' },
                        { value: 'less', label: 'Less Than' },
                        { value: 'equal', label: 'Equal To' },
                        { value: 'greaterEqual', label: 'Equal/Greater Than' },
                        { value: 'lessEqual', label: 'Equal/Less Than' }
                      ].map(option => (
                        <div
                          key={option.value}
                          onClick={() => {
                            setViewAllFilters(prev => ({ ...prev, stockComparison: option.value }));
                            setStockFilterDropdownOpen(false);
                          }}
                          style={{
                            padding: '0.75rem 1rem',
                            cursor: 'pointer',
                            color: '#ffffff',
                            backgroundColor: viewAllFilters.stockComparison === option.value ? '#3a3a3a' : 'transparent',
                            transition: 'background-color 0.15s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = viewAllFilters.stockComparison === option.value ? '#3a3a3a' : 'transparent'}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {viewAllFilters.stockComparison !== 'any' && (
                  <div style={{ width: '100px' }}>
                    <input
                      type="number"
                      min="0"
                      placeholder="0"
                      value={viewAllFilters.stockValue}
                      onChange={(e) => setViewAllFilters(prev => ({ ...prev, stockValue: e.target.value }))}
                      style={{
                        width: '100%',
                        backgroundColor: '#0a0a0a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        padding: '0.5rem 1rem',
                        height: '38px',
                        color: '#ffffff',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'box-shadow 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #3b198e'}
                      onBlur={(e) => e.target.style.boxShadow = ''}
                    />
                  </div>
                )}
                {viewAllFilters.stockComparison !== 'any' && (
                  <div 
                    onClick={() => setViewAllFilters(prev => ({ ...prev, showOnlyMatchingVariants: !prev.showOnlyMatchingVariants }))}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer',
                      padding: '0.5rem 0'
                    }}
                  >
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid #4a4a4a',
                      borderRadius: '4px',
                      backgroundColor: viewAllFilters.showOnlyMatchingVariants ? '#3b198e' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'background-color 0.15s ease'
                    }}>
                      {viewAllFilters.showOnlyMatchingVariants && (
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                      )}
                    </div>
                    <span style={{ color: '#9ca3af', fontSize: '13px', whiteSpace: 'nowrap' }}>Only matching variants</span>
                  </div>
                )}
                <button
                  onClick={() => {
                    setViewAllFilters({
                      selectedProducts: [],
                      searchQuery: '',
                      stockComparison: 'any',
                      stockValue: '',
                      showOnlyMatchingVariants: false
                    });
                    setProductFilterDropdownOpen(false);
                    setStockFilterDropdownOpen(false);
                  }}
                  style={{
                    backgroundColor: '#4a4a4a',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '0.5rem 1rem',
                    fontSize: '14px',
                    cursor: 'pointer',
                    marginLeft: 'auto',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a5a5a'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4a4a4a'}
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div style={{ 
              flex: 1, 
              overflowY: 'auto',
              paddingRight: '8px',
              borderTop: '1px solid #4a4a4a',
              paddingTop: '16px'
            }}>
              {products
                .filter(product => {
                  // Filter by selected products
                  if (viewAllFilters.selectedProducts.length > 0 && !viewAllFilters.selectedProducts.includes(product.id)) {
                    return false;
                  }
                  // Filter by search query
                  if (viewAllFilters.searchQuery && !product.name.toLowerCase().includes(viewAllFilters.searchQuery.toLowerCase())) {
                    return false;
                  }
                  // Filter by stock (check if any variant matches)
                  if (viewAllFilters.stockComparison !== 'any' && viewAllFilters.stockValue !== '') {
                    const stockVal = parseInt(viewAllFilters.stockValue);
                    const hasMatchingVariant = product.variants?.some(variant => {
                      if (variant.stock_type !== 'manual') return false;
                      const variantStock = stockCounts[variant.id]?.available ?? 0;
                      switch (viewAllFilters.stockComparison) {
                        case 'greater': return variantStock > stockVal;
                        case 'less': return variantStock < stockVal;
                        case 'equal': return variantStock === stockVal;
                        case 'greaterEqual': return variantStock >= stockVal;
                        case 'lessEqual': return variantStock <= stockVal;
                        default: return true;
                      }
                    });
                    if (!hasMatchingVariant) return false;
                  }
                  return true;
                })
                .map((product, productIndex, filteredProducts) => (
                <div key={product.id} style={{ 
                  marginBottom: productIndex === filteredProducts.length - 1 ? 0 : '24px'
                }}>
                  {/* Product Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      backgroundColor: '#3b198e'
                    }} />
                    <h4 style={{ 
                      color: '#ffffff', 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      margin: 0,
                      flex: 1
                    }}>
                      {product.name}
                    </h4>
                    {getStatusBadge(product.status)}
                    {product.category && (
                      <span style={{
                        backgroundColor: '#2a2a2a',
                        color: '#9ca3af',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '11px'
                      }}>
                        {product.category}
                      </span>
                    )}
                  </div>

                  {/* Variants List */}
                  {product.variants && product.variants.length > 0 ? (
                    <div style={{ 
                      marginLeft: '20px',
                      borderLeft: '2px solid #3a3a3a',
                      paddingLeft: '16px'
                    }}>
                      {product.variants
                        .filter(variant => {
                          // If showOnlyMatchingVariants is enabled and stock filter is active, filter variants
                          if (viewAllFilters.showOnlyMatchingVariants && viewAllFilters.stockComparison !== 'any' && viewAllFilters.stockValue !== '') {
                            if (variant.stock_type !== 'manual') return false;
                            const variantStock = stockCounts[variant.id]?.available ?? 0;
                            const stockVal = parseInt(viewAllFilters.stockValue);
                            switch (viewAllFilters.stockComparison) {
                              case 'greater': return variantStock > stockVal;
                              case 'less': return variantStock < stockVal;
                              case 'equal': return variantStock === stockVal;
                              case 'greaterEqual': return variantStock >= stockVal;
                              case 'lessEqual': return variantStock <= stockVal;
                              default: return true;
                            }
                          }
                          return true;
                        })
                        .map((variant, variantIndex, filteredVariants) => (
                        <div key={variant.id} style={{
                          backgroundColor: '#0a0a0a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '8px',
                          padding: '12px 16px',
                          marginBottom: variantIndex === filteredVariants.length - 1 ? 0 : '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px',
                              marginBottom: '4px'
                            }}>
                              <span style={{ 
                                color: '#e0e0e0', 
                                fontWeight: '500',
                                fontSize: '14px'
                              }}>
                                {variant.name}
                              </span>
                              <span style={{
                                backgroundColor: variant.stock_type === 'manual' ? '#7c3aed' : '#3b82f6',
                                color: '#ffffff',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                fontWeight: '500'
                              }}>
                                {variant.stock_type === 'manual' ? 'Manual' : 'API'}
                              </span>
                            </div>
                            <p style={{ 
                              color: '#9ca3af', 
                              fontSize: '12px', 
                              margin: '0 0 4px 0'
                            }}>
                              Stock: {variant.stock_type === 'manual' 
                                ? (stockCounts[variant.id] 
                                    ? `${stockCounts[variant.id].available} available` 
                                    : 'Loading...')
                                : 'N/A (API)'}
                            </p>
                            {variant.description && (
                              <p style={{ 
                                color: '#6b7280', 
                                fontSize: '12px', 
                                margin: 0 
                              }}>
                                {variant.description}
                              </p>
                            )}
                          </div>
                          <div style={{ 
                            textAlign: 'right',
                            marginLeft: '16px'
                          }}>
                            <span style={{ 
                              color: '#22c55e', 
                              fontWeight: '600',
                              fontSize: '15px'
                            }}>
                              ${parseFloat(variant.price).toFixed(2)}
                            </span>
                            {variant.backend_cost > 0 && (
                              <p style={{ 
                                color: '#6b7280', 
                                fontSize: '11px', 
                                margin: '2px 0 0 0' 
                              }}>
                                Backend Cost: ${parseFloat(variant.backend_cost).toFixed(2)}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ 
                      color: '#6b7280', 
                      fontSize: '13px', 
                      marginLeft: '20px',
                      fontStyle: 'italic'
                    }}>
                      No variants
                    </p>
                  )}
                </div>
              ))}
            </div>

            <div style={{ 
              marginTop: '20px',
              paddingTop: '16px',
              borderTop: '1px solid #4a4a4a'
            }}>
              <button
                onClick={() => setShowAllProductsModal(false)}
                style={{
                  width: '100%',
                  backgroundColor: '#4a4a4a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a5a5a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4a4a4a'}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {showDescriptionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ color: '#ffffff', fontSize: '20px', fontWeight: 'bold', marginBottom: '24px' }}>
              Edit Product Description - {selectedProduct?.name}
            </h2>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Requirements
              </label>
              <textarea
                value={productForm.description_data.requirements}
                onChange={(e) => setProductForm({ 
                  ...productForm, 
                  description_data: { ...productForm.description_data, requirements: e.target.value }
                })}
                rows={3}
                placeholder="Enter requirements information..."
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Information
              </label>
              <textarea
                value={productForm.description_data.information}
                onChange={(e) => setProductForm({ 
                  ...productForm, 
                  description_data: { ...productForm.description_data, information: e.target.value }
                })}
                rows={3}
                placeholder="Enter additional information..."
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Aimbot
              </label>
              <textarea
                value={productForm.description_data.aimbot}
                onChange={(e) => setProductForm({ 
                  ...productForm, 
                  description_data: { ...productForm.description_data, aimbot: e.target.value }
                })}
                rows={3}
                placeholder="Enter aimbot information..."
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Visuals
              </label>
              <textarea
                value={productForm.description_data.visuals}
                onChange={(e) => setProductForm({ 
                  ...productForm, 
                  description_data: { ...productForm.description_data, visuals: e.target.value }
                })}
                rows={3}
                placeholder="Enter visuals information..."
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                World
              </label>
              <textarea
                value={productForm.description_data.world}
                onChange={(e) => setProductForm({ 
                  ...productForm, 
                  description_data: { ...productForm.description_data, world: e.target.value }
                })}
                rows={3}
                placeholder="Enter world information..."
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Configs
              </label>
              <textarea
                value={productForm.description_data.configs}
                onChange={(e) => setProductForm({ 
                  ...productForm, 
                  description_data: { ...productForm.description_data, configs: e.target.value }
                })}
                rows={3}
                placeholder="Enter configs information..."
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Misc
              </label>
              <textarea
                value={productForm.description_data.misc}
                onChange={(e) => setProductForm({ 
                  ...productForm, 
                  description_data: { ...productForm.description_data, misc: e.target.value }
                })}
                rows={3}
                placeholder="Enter misc information..."
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowDescriptionModal(false)}
                style={{
                  flex: 1,
                  backgroundColor: '#4a4a4a',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a5a5a'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#4a4a4a'}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDescription}
                style={{
                  flex: 1,
                  backgroundColor: '#3b198e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2d1270'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3b198e'}
              >
                Save Description
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
